import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { X, UserCheck, Users, Shield, ArrowRight, Loader2, Sparkles, CheckCircle, UserPlus, Check } from "lucide-react";
import { StudioRole } from "../../../../Data/role.dto";
import { Member } from "../../../../Data/member.dto";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Hooks";
import { assignRoleAPI, getAllRolesAPI } from "../../../../Redux/ApiCalls/Dashboard/RoleApi";
import { getAllMembers } from "../../../../Redux/ApiCalls/Dashboard/MembersAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { useNavigate } from "react-router-dom";
import { IMAGE_CDN } from "../../../../config/config";
import AddMemberModal from "../../../molecules/Dashboard/Member/AddMemberModal";

interface AssignRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialRoleId?: number | string | null;
  initialMemberId?: number | string | null;
  onAssignedSuccess?: () => void;
}

const gradient =
  "linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%)";

export const AssignRoleModal: React.FC<AssignRoleModalProps> = ({
  isOpen,
  onClose,
  initialRoleId,
  initialMemberId,
  onAssignedSuccess,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { roles, loading } = useAppSelector((state) => state.role);
  const { members, isMemberFetched } = useAppSelector((state) => state.member);

  const [selectedRoleId, setSelectedRoleId] = useState<number | string>("");
  const [selectedMemberIds, setSelectedMemberIds] = useState<(number | string)[]>([]);
  const [isAssigning, setIsAssigning] = useState<boolean>(false);
  const [searchMember, setSearchMember] = useState("");
  const [assignedResult, setAssignedResult] = useState<any | null>(null);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      if (!roles.length) {
        dispatch(getAllRolesAPI());
      }
      if (!isMemberFetched) {
        dispatch(getAllMembers());
      }
      if (initialRoleId) {
        setSelectedRoleId(initialRoleId);
      } else if (roles.length > 0) {
        setSelectedRoleId(roles[0].id);
      }
      if (initialMemberId) {
        setSelectedMemberIds([initialMemberId]);
      } else {
        setSelectedMemberIds([]);
      }
      setIsAssigning(false);
      setAssignedResult(null);
      setSearchMember("");
      setIsAddMemberOpen(false);
    }
  }, [isOpen, initialRoleId, initialMemberId, roles.length, isMemberFetched, dispatch]);

  // Set of all team member IDs, phone numbers, and user IDs that are already assigned to ANY role
  const assignedMemberIds = new Set<string>();
  const assignedMemberPhones = new Set<string>();
  const assignedUserIds = new Set<string>();

  (roles || []).forEach((r) => {
    (r.assigned_members || []).forEach((am: any) => {
      if (am.id !== undefined && am.id !== null) assignedMemberIds.add(String(am.id));
      if (am.user_id !== undefined && am.user_id !== null) assignedUserIds.add(String(am.user_id));
      if (am.phone_number) assignedMemberPhones.add(String(am.phone_number).trim());
    });
  });

  const isMemberAlreadyAssigned = (m: any): boolean => {
    if (m.role && (m.role.id || m.role.name)) return true;
    if (assignedMemberIds.has(String(m.id))) return true;
    if (m.user_id && assignedUserIds.has(String(m.user_id))) return true;
    if (m.phone_number && assignedMemberPhones.has(String(m.phone_number).trim())) return true;
    return false;
  };

  const filteredMembers = (members || []).filter((m) => {
    // Exclude members that already have ANY role assigned, unless they are the initialMemberId
    const isInitial = initialMemberId && String(m.id) === String(initialMemberId);
    if (!isInitial && isMemberAlreadyAssigned(m)) return false;

    const q = searchMember.toLowerCase().trim();
    if (!q) return true;
    return (
      m.name?.toLowerCase().includes(q) ||
      m.phone_number?.includes(q) ||
      m.job_type?.toLowerCase().includes(q)
    );
  });

  const toggleMemberSelection = (memberId: number | string) => {
    setSelectedMemberIds((prev) => {
      const exists = prev.some((id) => String(id) === String(memberId));
      if (exists) {
        return prev.filter((id) => String(id) !== String(memberId));
      } else {
        return [...prev, memberId];
      }
    });
  };

  const isAllFilteredSelected =
    filteredMembers.length > 0 &&
    filteredMembers.every((m) =>
      selectedMemberIds.some((id) => String(id) === String(m.id))
    );

  const handleSelectAllFiltered = () => {
    if (isAllFilteredSelected) {
      const filteredIdSet = new Set(filteredMembers.map((m) => String(m.id)));
      setSelectedMemberIds((prev) =>
        prev.filter((id) => !filteredIdSet.has(String(id)))
      );
    } else {
      setSelectedMemberIds((prev) => {
        const set = new Set(prev.map((id) => String(id)));
        filteredMembers.forEach((m) => set.add(String(m.id)));
        return (members || [])
          .filter((m) => set.has(String(m.id)))
          .map((m) => m.id);
      });
    }
  };

  const currentRole = roles.find((r) => String(r.id) === String(selectedRoleId));

  const handleAssign = async () => {
    if (!selectedRoleId) {
      showErrorToast("Please select a role to assign");
      return;
    }
    if (selectedMemberIds.length === 0) {
      showErrorToast("Please select at least one team member");
      return;
    }

    const validMemberIds: (number | string)[] = [];
    const alreadyAssignedNames: string[] = [];

    for (const memberId of selectedMemberIds) {
      const targetMember = (members || []).find((m) => String(m.id) === String(memberId));
      const isInitial = initialMemberId && String(memberId) === String(initialMemberId);
      if (!isInitial && targetMember && isMemberAlreadyAssigned(targetMember)) {
        alreadyAssignedNames.push(targetMember.name || `Member #${memberId}`);
      } else {
        validMemberIds.push(memberId);
      }
    }

    if (alreadyAssignedNames.length > 0 && validMemberIds.length === 0) {
      showErrorToast("Selected member(s) are already assigned to a role. At a time only 1 role can be assigned.");
      return;
    }

    setIsAssigning(true);
    let successCount = 0;
    const errors: string[] = [];
    let lastResultData: any = null;
    const assignedNames: string[] = [];

    for (const memberId of validMemberIds) {
      try {
        const res = await dispatch(
          assignRoleAPI({
            roleId: selectedRoleId,
            teamMemberId: memberId,
          })
        ).unwrap();

        successCount++;
        lastResultData = res?.result?.data || res?.result || {};
        const m = (members || []).find((mem) => String(mem.id) === String(memberId));
        if (m?.name) assignedNames.push(m.name);
      } catch (err: any) {
        const m = (members || []).find((mem) => String(mem.id) === String(memberId));
        const memberName = m?.name || `Member #${memberId}`;
        const errorMsg =
          (typeof err === "string" ? err : null) ||
          err?.message ||
          err?.error ||
          err?.detail ||
          `Failed for ${memberName}`;
        errors.push(errorMsg);
      }
    }

    setIsAssigning(false);

    if (successCount > 0) {
      setAssignedResult({
        ...lastResultData,
        count: successCount,
        assignedNames,
      });
      showSuccessToast(
        successCount === 1
          ? (lastResultData?.login_hint || "Role assigned successfully! Sub-user account is ready.")
          : `Role assigned successfully to ${successCount} team members!`
      );
      dispatch(getAllRolesAPI());
      dispatch(getAllMembers());
      if (onAssignedSuccess) onAssignedSuccess();
    }

    if (errors.length > 0) {
      showErrorToast(errors.join(". "));
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={isAssigning ? undefined : onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <HeaderTitleWrap>
            <IconWrap>
              <UserCheck size={20} color="#c94daa" />
            </IconWrap>
            <div>
              <ModalTitle>Assign Role to Members</ModalTitle>
              <ModalSubtitle>Configure studio permissions for team members</ModalSubtitle>
            </div>
          </HeaderTitleWrap>
          <CloseButton type="button" onClick={onClose} disabled={isAssigning}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {assignedResult ? (
            <SuccessState>
              <SuccessIconWrap>
                <CheckCircle size={38} color="#22c55e" />
              </SuccessIconWrap>
              <SuccessTitle>
                {assignedResult.count > 1 ? "Roles Assigned Successfully!" : "Role Assigned Successfully!"}
              </SuccessTitle>
              <SuccessText>
                {assignedResult.count > 1 ? (
                  <>
                    <strong>{assignedResult.count} team members</strong> (
                    {assignedResult.assignedNames?.slice(0, 3).join(", ")}
                    {assignedResult.assignedNames?.length > 3
                      ? `, +${assignedResult.assignedNames.length - 3} more`
                      : ""}
                    ) now have the <strong>{currentRole?.name}</strong> role.
                  </>
                ) : (
                  <>
                    {assignedResult.assignedNames?.[0] || "The team member"} now has the{" "}
                    <strong>{currentRole?.name}</strong> role.
                  </>
                )}
              </SuccessText>
              <HintBox>
                <Sparkles size={16} color="#c94daa" />
                <span>
                  {assignedResult.login_hint ||
                    (assignedResult.count > 1
                      ? "The members can log in to KwikMemories dashboard directly with their phone numbers & OTP (no separate registration required)."
                      : "The member can log in to KwikMemories dashboard directly with their phone number & OTP (no separate registration required).")}
                </span>
              </HintBox>

              <ButtonGroup>
                <SecondaryBtn
                  type="button"
                  onClick={() => {
                    setAssignedResult(null);
                    onClose();
                  }}
                >
                  Done
                </SecondaryBtn>
                <PrimaryBtn
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate("/dashboard/members/all");
                  }}
                >
                  View in Members
                  <ArrowRight size={15} />
                </PrimaryBtn>
              </ButtonGroup>
            </SuccessState>
          ) : (
            <>
              {/* Select Role */}
              <FormGroup>
                <Label>
                  <Shield size={14} color="#c94daa" />
                  Select Studio Role
                </Label>
                <Select
                  value={selectedRoleId}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                >
                  <option value="" disabled>
                    -- Choose a role --
                  </option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.permissions?.length || 0} permissions)
                    </option>
                  ))}
                </Select>
                {currentRole && (
                  <RoleMetaInfo>
                    <span>
                      Permissions enabled: <strong>{currentRole.permissions?.length || 0}</strong> keys
                    </span>
                    {currentRole.description && <span> · {currentRole.description}</span>}
                  </RoleMetaInfo>
                )}
              </FormGroup>

              {/* Select Member */}
              <FormGroup>
                <LabelHeader>
                  <Label>
                    <Users size={14} color="#c94daa" />
                    Select Team Members
                    {selectedMemberIds.length > 0 && (
                      <SelectedCountPill>
                        {selectedMemberIds.length} selected
                      </SelectedCountPill>
                    )}
                  </Label>
                  <HeaderActionsWrap>
                    {filteredMembers.length > 0 && (
                      <SelectAllBtn
                        type="button"
                        onClick={handleSelectAllFiltered}
                      >
                        {isAllFilteredSelected ? "Deselect All" : "Select All"}
                      </SelectAllBtn>
                    )}
                    <AddMemberBtn
                      type="button"
                      onClick={() => setIsAddMemberOpen(true)}
                    >
                      <UserPlus size={13} />
                      <span>Add Member</span>
                    </AddMemberBtn>
                  </HeaderActionsWrap>
                </LabelHeader>
                <SearchInput
                  type="text"
                  placeholder="Search by name, phone, or job title..."
                  value={searchMember}
                  onChange={(e) => setSearchMember(e.target.value)}
                />

                <MemberList>
                  {filteredMembers.length === 0 ? (
                    <EmptyMembers>
                      <EmptyMembersText>
                        {(members || []).length === 0
                          ? "No team members found in your studio."
                          : searchMember.trim()
                          ? "No unassigned team members match your search."
                          : "All team members are already assigned a role. Unassign a member first to reassign them."}
                      </EmptyMembersText>
                      <EmptyAddMemberBtn
                        type="button"
                        onClick={() => setIsAddMemberOpen(true)}
                      >
                        <UserPlus size={13} />
                        <span>Add Team Member</span>
                      </EmptyAddMemberBtn>
                    </EmptyMembers>
                  ) : (
                    filteredMembers.map((member) => {
                      const isSelected = selectedMemberIds.some(
                        (id) => String(id) === String(member.id)
                      );
                      const avatarSrc = member.profile_image
                        ? `${IMAGE_CDN}/${member.profile_image}`
                        : "";
                      const memberCurrentRole =
                        roles.find((r) =>
                          r.assigned_members?.some(
                            (am) => String(am.id) === String(member.id)
                          )
                        ) ||
                        ((member as any).role
                          ? roles.find(
                              (r) => r.id === (member as any).role?.id
                            )
                          : null);

                      return (
                        <MemberItem
                          key={member.id}
                          $selected={isSelected}
                          onClick={() => toggleMemberSelection(member.id)}
                        >
                          <AvatarWrap>
                            {avatarSrc ? (
                              <Avatar src={avatarSrc} alt={member.name} />
                            ) : (
                              <AvatarFallback>
                                {member.name?.charAt(0)?.toUpperCase() || "M"}
                              </AvatarFallback>
                            )}
                          </AvatarWrap>

                          <MemberDetails>
                            <MemberNameRow>
                              <MemberName>{member.name}</MemberName>
                              {memberCurrentRole && (
                                <MemberRoleBadge>
                                  <Shield size={10} />
                                  <span>{memberCurrentRole.name}</span>
                                </MemberRoleBadge>
                              )}
                            </MemberNameRow>
                            <MemberSub>
                              {member.country_code} {member.phone_number}
                              {member.job_type ? ` · ${member.job_type}` : ""}
                            </MemberSub>
                          </MemberDetails>

                          <CheckboxSquare $checked={isSelected}>
                            {isSelected && <Check size={12} color="#ffffff" strokeWidth={3} />}
                          </CheckboxSquare>
                        </MemberItem>
                      );
                    })
                  )}
                </MemberList>
              </FormGroup>

              {/* Informational Hint */}
              <HintCard>
                <Sparkles size={16} color="#c94daa" />
                <HintText>
                  Assigning a role auto-creates a <strong>Sub-user</strong> account. The members will be able to log in with their phone number and receive OTP directly.
                </HintText>
              </HintCard>

              {/* Actions */}
              <ModalFooter>
                <SecondaryBtn
                  type="button"
                  onClick={onClose}
                  disabled={isAssigning || loading.assign}
                >
                  Cancel
                </SecondaryBtn>
                <PrimaryBtn
                  type="button"
                  onClick={handleAssign}
                  disabled={
                    isAssigning ||
                    loading.assign ||
                    !selectedRoleId ||
                    selectedMemberIds.length === 0
                  }
                >
                  {isAssigning || loading.assign ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Assigning...
                    </>
                  ) : selectedMemberIds.length > 1 ? (
                    `Assign Role (${selectedMemberIds.length})`
                  ) : (
                    "Assign Role"
                  )}
                </PrimaryBtn>
              </ModalFooter>
            </>
          )}
        </ModalBody>
      </ModalContainer>

      <AddMemberModal
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        onMemberCreated={(newMember: Member) => {
          if (newMember?.id) {
            setSelectedMemberIds((prev) => [...prev, newMember.id]);
          }
        }}
      />
    </Overlay>
  );
};

export default AssignRoleModal;

/* Styled Components */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  max-width: 520px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const HeaderTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #171717;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #737373;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #737373;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #171717;
    background: #f5f5f5;
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #262626;
`;

const LabelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectedCountPill = styled.span`
  background: #fdf2f8;
  color: #c94daa;
  border: 1px solid rgba(201, 77, 170, 0.25);
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 9999px;
  margin-left: 6px;
`;

const HeaderActionsWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectAllBtn = styled.button`
  background: transparent;
  color: #c94daa;
  border: none;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
  transition: all 0.15s ease;

  &:hover {
    background: #fdf2f8;
    color: #a8388d;
  }
`;

const AddMemberBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fdf2f8;
  color: #c94daa;
  border: 1px solid rgba(201, 77, 170, 0.25);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fce7f3;
    border-color: #c94daa;
    transform: translateY(-0.5px);
  }
`;

const Select = styled.select`
  width: 100%;
  height: 42px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  padding: 0 12px;
  font-size: 14px;
  color: #171717;
  background: #ffffff;
  outline: none;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }
`;

const RoleMetaInfo = styled.div`
  font-size: 12px;
  color: #737373;
  margin-top: 2px;

  strong {
    color: #c94daa;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  padding: 0 12px;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #c94daa;
    box-shadow: 0 0 0 2px rgba(201, 77, 170, 0.1);
  }
`;

const MemberList = styled.div`
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EmptyMembers = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const EmptyMembersText = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.4;
`;

const EmptyAddMemberBtn = styled.button`
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #ffffff;
  color: #c94daa;
  border: 1px dashed rgba(201, 77, 170, 0.5);
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fdf2f8;
    border-color: #c94daa;
  }
`;

const MemberItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  background: ${(props) => (props.$selected ? "#fdf2f8" : "transparent")};
  border: 1px solid ${(props) => (props.$selected ? "rgba(201, 77, 170, 0.3)" : "transparent")};
  transition: all 0.15s;

  &:hover {
    background: ${(props) => (props.$selected ? "#fdf2f8" : "#fafafa")};
  }
`;

const AvatarWrap = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #c94daa;
`;

const MemberDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const MemberNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MemberName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MemberRoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #c94daa;
  background: #fdf2f8;
  border: 1px solid rgba(201, 77, 170, 0.18);
  padding: 1px 6px;
  border-radius: 9999px;
  white-space: nowrap;
`;

const MemberSub = styled.div`
  font-size: 11px;
  color: #737373;
`;

const CheckboxSquare = styled.div<{ $checked: boolean }>`
  width: 19px;
  height: 19px;
  border-radius: 5px;
  border: 1.5px solid ${(props) => (props.$checked ? "#c94daa" : "#d4d4d4")};
  background: ${(props) => (props.$checked ? gradient : "#ffffff")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  box-shadow: ${(props) =>
    props.$checked ? "0 2px 6px rgba(201, 77, 170, 0.25)" : "none"};
`;

const HintCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #faf5ff;
  border: 1px solid rgba(201, 77, 170, 0.15);
  border-radius: 12px;
`;

const HintText = styled.p`
  font-size: 12px;
  color: #525252;
  margin: 0;
  line-height: 1.4;

  strong {
    color: #171717;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

const SecondaryBtn = styled.button`
  padding: 9px 18px;
  border-radius: 10px;
  border: 1px solid #e5e5e5;
  background: #ffffff;
  color: #525252;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #171717;
  }
`;

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 10px;
  border: none;
  background: ${gradient};
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(206, 86, 176, 0.25);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 0;
`;

const SuccessIconWrap = styled.div`
  margin-bottom: 12px;
`;

const SuccessTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #171717;
  margin: 0 0 6px;
`;

const SuccessText = styled.p`
  font-size: 13px;
  color: #525252;
  margin: 0 0 16px;

  strong {
    color: #c94daa;
  }
`;

const HintBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: #fdf2f8;
  border-radius: 10px;
  font-size: 12px;
  color: #404040;
  text-align: left;
  line-height: 1.4;
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: center;
`;
